/**
 * Hydration callers for the APIM search experience.
 *
 * Three context-specific entry points that orchestrate the underlying
 * `queryProductList` + `queryStoreList` modules:
 *
 *   - hydrateTypeAhead — used by the header search bar (small page sizes,
 *     fires both queries in parallel so the dropdown shows products AND
 *     matching stores side-by-side).
 *   - hydratePLP      — used by PLP / category templates.
 *   - hydrateSLP      — used by the search results page when the user
 *     submits the form (Enter key) and lands on /search?q=…
 *
 * All three return the same shape:
 *
 *   {
 *     query: string,
 *     products: import('@theme/query-product-list').QueryProductListResult,
 *     stores:   import('@theme/query-store-list').QueryStoreListResult
 *   }
 *
 * Each envelope (`products` and `stores`) follows the
 *   { ok, data, error }
 * convention defined by the underlying modules. Zero-result responses are
 * treated as `ok: true` per the MA-799 acceptance criteria — only HTTP /
 * network / config failures land on the error branch.
 *
 * Every hydration call also dispatches a `theme:search:hydrated` CustomEvent
 * on `document` so additional listeners (e.g. analytics) can react without
 * the caller having to thread callbacks around.
 *
 * SLP lifecycle (MA-1367):
 *
 *   1. The page-template API resolves FIRST. Its `redirectUrl` supersedes
 *      everything — the browser redirects immediately, no search fires, and a
 *      `theme:search:redirect` event is dispatched instead of the usual ones.
 *   2. Its `moduleOrder` decides which searches run at all: a module absent
 *      from the array is never fetched (an empty array — the API's
 *      zero-results template — skips both). A page-template failure or a
 *      missing `moduleOrder` must never block search: both queries run and
 *      consumers fall back to their defaults.
 *   3. The remaining searches run in parallel, and each dispatches an eager
 *      `theme:search:products:hydrated` / `theme:search:stores:hydrated`
 *      event the moment it resolves — one response never render-blocks the
 *      other. The combined `theme:search:hydrated` event still fires once
 *      after all resolve (filters, analytics, and structured-data rely on it).
 */

import { queryProductList } from '@theme/query-product-list';
import { queryStoreList } from '@theme/query-store-list';
import { queryProductDetail } from '@theme/query-product-detail';
import { queryStoreDetail } from '@theme/query-store-detail';
import { queryPageTemplate } from '@theme/query-page-template';
import { getApiLocale, getStoreApiLocale } from '@theme/locale-map';
import {
  track,
  flattenRawMeta,
  flattenRequest,
  getLastSearch,
  buildProductsAnalyticsData,
  registerAnalyticsProducts,
  ANALYTICS_EVENTS,
} from '@theme/analytics';

// Re-exported so consumers (filter UI, bootstrap script) request the exact
// aggregation keys the API needs to include the `filters` object.
export { FILTER_AGGREGATIONS } from '@theme/query-product-list';

/**
 * URL search-param keys that are NOT product filters and must be skipped when
 * deriving the APIM `filters` object from the query string. Covers the search
 * term, sort, pagination, Shopify's native search-form params (`type`,
 * `options[prefix]`, `options[unavailable_products]`), Shopify preview/section
 * params, and the usual marketing/click-id params so a stray `?type=product` or
 * `?utm_source=…` is never sent as a filter (which could 400 the product
 * search).
 */
export const NON_FILTER_PARAMS = new Set([
  'q',
  'sort',
  'page',
  'view',
  'type',
  'options[prefix]',
  'options[unavailable_products]',
  'preview_theme_id',
  'section_id',
  '_fd',
  '_ab',
  '_ke',
  'fbclid',
  'gclid',
  'srsltid',
  'mc_cid',
  'mc_eid',
]);

/**
 * Parses the APIM product `filters` map and `sort` value out of URL search
 * params. The storefront encodes applied filters as `requestKey=value`
 * (repeated for multi-select, e.g. `?node.id=1&node.id=2&isOnSale=true`) and
 * sort as `sort=<identifier>` — matching the live site's shareable URLs.
 *
 * Every param that is not in {@link NON_FILTER_PARAMS} (and is not a marketing
 * prefix like `utm_`) becomes a filter; each value is collected into an array
 * of strings, which is the shape the product-search API requires.
 *
 * @param {URLSearchParams} params
 * @returns {{ filters: Record<string, string[]>, sort: string | undefined }}
 */
export function productFiltersFromParams(params) {
  /** @type {Record<string, string[]>} */
  const filters = {};
  let sort;

  for (const [key, value] of params) {
    if (key === 'sort') {
      if (value) sort = value;
      continue;
    }
    if (value === '' || NON_FILTER_PARAMS.has(key) || key.startsWith('utm_')) continue;

    if (!filters[key]) filters[key] = [];
    filters[key].push(value);
  }

  return { filters, sort };
}

/**
 * @typedef {'typeahead' | 'plp' | 'slp'} HydrateContext
 *
 * @typedef {Object} HydrateParams
 * @property {string}              query             - Search term. Empty string is allowed for stores (browse).
 * @property {number}              [page]            - 1-based page number.
 * @property {number}              [size]            - Results per page (applied to both queries).
 * @property {number}              [productSize]     - Override page size for products only.
 * @property {number}              [storeSize]       - Override page size for stores only.
 * @property {Object}              [productParams]   - Extra params passed through to queryProductList.
 * @property {Object}              [storeParams]     - Extra params passed through to queryStoreList.
 * @property {AbortSignal}         [signal]          - Abort signal forwarded to both queries.
 * @property {boolean}             [skipProducts]    - If true, skip the product query (stores only).
 * @property {boolean}             [skipStores]      - If true, skip the store query (products only).
 * @property {boolean}             [includePageTemplate] - If true, resolve the page-template API first: its redirectUrl short-circuits the lifecycle and its moduleOrder gates which searches fire.
 *
 * @typedef {Object} HydrateResult
 * @property {string}                                                                query
 * @property {HydrateContext}                                                        context
 * @property {import('@theme/query-product-list').QueryProductListResult | null}     products
 * @property {import('@theme/query-store-list').QueryStoreListResult | null}         stores
 * @property {import('@theme/query-page-template').QueryPageTemplateResult | null}   pageTemplate
 * @property {boolean}                                                               [redirected]  - True when the page-template `redirectUrl` short-circuited the lifecycle.
 * @property {string}                                                                [redirectUrl] - The resolved redirect target (only set with `redirected`).
 */

const isAbort = (error) => error instanceof DOMException && error.name === 'AbortError';

/**
 * Coerces a thrown error from a rejected query into the standard error envelope.
 * Re-throws AbortError so the orchestrator can short-circuit on cancellation.
 *
 * @param {string} kind - 'product' | 'store' (used in the fallback message)
 * @param {unknown} error
 * @returns {{ ok: false, data: null, error: { message: string, status: null } }}
 */
const wrapThrown = (kind, error) => {
  if (isAbort(error)) throw error;
  return {
    ok: false,
    data: null,
    error: {
      message: (error && /** @type {Error} */ (error).message) || `${kind} search failed.`,
      status: null,
    },
  };
};

/**
 * Session cache of page-template lookups keyed by search query. Filter and
 * pagination refetches reuse the first verdict (redirect / module order can't
 * change mid-session for the same query) instead of paying the sequential
 * page-template round-trip on every fetch. Failed lookups are evicted so a
 * later refetch can recover.
 *
 * @type {Map<string, Promise<import('@theme/query-page-template').QueryPageTemplateResult>>}
 */
const pageTemplateCache = new Map();

/**
 * Resolves the page-template verdict for a query, deduped per session.
 * Deliberately not abortable: the result is shared across callers, and a
 * pagination/filter abort must not poison the cached promise.
 *
 * @param {string} query
 * @param {Object} locale
 * @returns {Promise<import('@theme/query-page-template').QueryPageTemplateResult>}
 */
const fetchPageTemplate = (query, locale) => {
  const key = query.trim();
  let promise = pageTemplateCache.get(key);
  if (!promise) {
    promise = queryPageTemplate({ query, ...locale })
      .then((result) => {
        if (!result.ok) pageTemplateCache.delete(key);
        return result;
      })
      .catch((error) => {
        pageTemplateCache.delete(key);
        return wrapThrown('Page template', error);
      });
    pageTemplateCache.set(key, promise);
  }
  return promise;
};

/**
 * Extracts and validates the page-template `redirectUrl`. Returns the
 * absolute target href, or '' when there is nothing (valid) to redirect to.
 * Only http(s) targets are honoured, and a redirect back to the current URL
 * is ignored — replace() on the same address would loop forever.
 *
 * @param {import('@theme/query-page-template').QueryPageTemplateResult | null} pageTemplate
 * @returns {string}
 */
const resolveRedirectUrl = (pageTemplate) => {
  const raw = pageTemplate?.ok ? pageTemplate.data.redirectUrl : '';
  if (typeof raw !== 'string' || !raw.trim()) return '';
  try {
    const url = new URL(raw.trim(), window.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    if (url.href === window.location.href) return '';
    return url.href;
  } catch {
    return '';
  }
};

/**
 * Runs the search lifecycle for a context and assembles the combined envelope.
 *
 * With `includePageTemplate` (SLP), the page-template API resolves first:
 * a `redirectUrl` short-circuits everything (immediate redirect, no searches),
 * and `moduleOrder` gates which searches fire at all. The surviving product /
 * store queries then run in parallel, each dispatching an eager
 * `theme:search:<module>:hydrated` event as it resolves so neither response
 * render-blocks the other. Either side can also be skipped explicitly via
 * `skipProducts` / `skipStores`; skipped envelopes come back `null`.
 *
 * @param {HydrateContext} context
 * @param {HydrateParams}  params
 * @returns {Promise<HydrateResult>}
 */
const orchestrate = async (context, params) => {
  const {
    query,
    page = 1,
    size,
    productSize,
    storeSize,
    productParams = {},
    storeParams = {},
    signal,
    skipProducts = false,
    skipStores = false,
    includePageTemplate = false,
  } = params;

  const locale = getApiLocale();
  const storeLocale = getStoreApiLocale();
  const safeQuery = typeof query === 'string' ? query : '';

  // Announce the start of the lifecycle so grid consumers can paint a loading
  // state (skeletons) for the whole in-flight window — initial load and every
  // re-fetch (filter, sort, pagination) funnels through here. Pairs with the
  // `theme:search:hydrated` event dispatched once the searches resolve.
  document.dispatchEvent(new CustomEvent('theme:search:loading', { detail: { context } }));

  // --- Phase 1 (SLP): page-template gates the whole lifecycle. ------------
  /** @type {import('@theme/query-page-template').QueryPageTemplateResult | null} */
  let pageTemplate = null;
  let productsGated = skipProducts;
  let storesGated = skipStores;

  if (includePageTemplate && safeQuery.trim()) {
    pageTemplate = await fetchPageTemplate(safeQuery, locale);

    // The shared page-template promise ignores aborts — honour the caller's
    // signal here instead, before any search fires.
    if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');

    const redirectUrl = resolveRedirectUrl(pageTemplate);
    if (redirectUrl) {
      /** @type {HydrateResult} */
      const redirectResult = {
        query: safeQuery,
        context,
        products: null,
        stores: null,
        pageTemplate,
        redirected: true,
        redirectUrl,
      };
      track(ANALYTICS_EVENTS.SEARCH_REDIRECT, { redirectUrl, query: safeQuery, searchContext: context });
      document.dispatchEvent(new CustomEvent('theme:search:redirect', { detail: redirectResult }));
      window.location.replace(redirectUrl);
      return redirectResult;
    }

    // `moduleOrder` names the modules that display — a search absent from it
    // is never fetched. An empty array is the API's zero-results template:
    // both searches are skipped and consumers render their empty states.
    const moduleOrder = pageTemplate?.ok && Array.isArray(pageTemplate.data.moduleOrder)
      ? pageTemplate.data.moduleOrder
      : null;
    if (moduleOrder) {
      productsGated = productsGated || !moduleOrder.includes('products');
      storesGated = storesGated || !moduleOrder.includes('stores');
    }
  }

  // --- Phase 2: the surviving searches run in parallel. -------------------
  const productPromise = productsGated
    ? Promise.resolve(null)
    : queryProductList({
        query,
        page,
        size: productSize ?? size,
        signal,
        ...locale,
        ...productParams,
      }).catch((error) => wrapThrown('Product', error));

  const storePromise = storesGated
    ? Promise.resolve(null)
    : queryStoreList({
        query,
        page,
        size: storeSize ?? size,
        signal,
        ...storeLocale,
        ...storeParams,
      }).catch((error) => wrapThrown('Store', error));

  // Eager per-module dispatch: each module's consumers render as soon as
  // their own data lands (a gated module resolves `null` immediately, letting
  // its consumer hide without waiting for the other search). The trailing
  // catch only swallows AbortError re-thrown by wrapThrown — the awaited
  // Promise.all below still propagates it to the caller.
  productPromise
    .then((products) => {
      const detail = { query: safeQuery, context, pageTemplate, products };
      window.__ngProductHydration = detail;
      if (products) trackProductSearch(safeQuery, context, products);
      document.dispatchEvent(new CustomEvent('theme:search:products:hydrated', { detail }));
    })
    .catch(() => {});

  storePromise
    .then((stores) => {
      const detail = { query: safeQuery, context, pageTemplate, stores };
      window.__ngStoreHydration = detail;
      if (stores) trackStoreSearch(safeQuery, context, stores);
      document.dispatchEvent(new CustomEvent('theme:search:stores:hydrated', { detail }));
    })
    .catch(() => {});

  const [products, stores] = await Promise.all([productPromise, storePromise]);

  /** @type {HydrateResult} */
  const result = { query: safeQuery, context, products, stores, pageTemplate };

  trackPageTemplate(safeQuery, context, pageTemplate);
  document.dispatchEvent(new CustomEvent('theme:search:hydrated', { detail: result }));

  return result;
};

/* ==================================================================
 * ANALYTICS
 *
 * The tracking calls live here, in the file that owns the search
 * lifecycle, rather than in a listener inside analytics.js — the request
 * and response shapes have exactly one owner, so there is no second copy
 * to keep in sync.
 * ================================================================*/

/**
 * Emits the product-search event.
 *
 * Request fields come from `data.request` — the exact body query-product-list.js
 * sent — so the event carries the API's own field names and values verbatim.
 *
 * Exported for the same reason as `trackStoreSearch`: more than one surface runs
 * a product search and each owns its own request. Sharing this builder keeps the
 * payload shape identical across them, and `searchContext` is what separates
 * them in a report.
 *
 * @param {string} query   - The sanitised search term.
 * @param {string} context - The search context (PLP / SLP / collection-cards / …).
 * @param {Object} products - The queryProductList result envelope.
 */
export function trackProductSearch(query, context, products) {
  const raw = products.data?.raw || {};
  const hits = products.data?.products || [];

  // Remember each returned product so a later click on its card can carry
  // the full product payload.
  registerAnalyticsProducts(hits);

  track(ANALYTICS_EVENTS.PRODUCT_SEARCH, {
    query,
    searchContext: context,
    totalResults: raw.totalResults ?? hits.length,
    productsReturned: hits.length,
    page: raw.page ?? 1,
    size: raw.size ?? hits.length,
    filters: raw.filters || null,
    sort: raw.sort || '',
    responseOk: products.ok ?? false,
    errorMessage: products.error?.message || '',
    products: buildProductsAnalyticsData(hits),
    ...flattenRequest(products.data?.request),
    ...flattenRawMeta(raw),
  });
}

/**
 * Emits the store-search event.
 *
 * Exported because there are three surfaces that run a store search and each
 * owns its own request: the search results page (via `orchestrate`), the
 * store-list template (`hydrateStoreList`), and the A–Z store directory, which
 * calls `queryStoreList` directly from its own custom element. They share this
 * one payload builder so the event shape cannot drift between them;
 * `searchContext` is what tells the three apart in a report.
 *
 * @param {string} query    - The sanitised search term.
 * @param {string} context  - The search context (`slp` / `store-list` / `store-directory`).
 * @param {Object} stores   - The queryStoreList result envelope.
 */
export function trackStoreSearch(query, context, stores) {
  const raw = stores.data?.raw || {};
  const hits = stores.data?.stores || [];

  track(ANALYTICS_EVENTS.STORE_SEARCH, {
    query,
    searchContext: context,
    totalResults: raw.totalResults ?? hits.length,
    storesReturned: hits.length,
    responseOk: stores.ok ?? false,
    errorMessage: stores.error?.message || '',
    ...flattenRequest(stores.data?.request),
    ...flattenRawMeta(raw),
  });
}

/**
 * Emits the page-template event, once per search lifecycle, when the
 * page-template API actually returned data.
 * @param {string} query   - The sanitised search term.
 * @param {string} context - The search context.
 * @param {Object|null} pageTemplate - The queryPageTemplate result envelope.
 */
function trackPageTemplate(query, context, pageTemplate) {
  if (!pageTemplate?.ok || !pageTemplate.data) return;
  const data = pageTemplate.data;

  track(ANALYTICS_EVENTS.PAGE_TEMPLATE, {
    searchContext: context,
    moduleOrder: data.moduleOrder || [],
    hasRedirect: !!data.redirectUrl,
    searchRedirectUrl: data.redirectUrl || '',
    ...flattenRequest(data.request),
    query: data.query || query,
    ...flattenRawMeta(data.raw || data),
  });
}

/**
 * Type-ahead caller — small page sizes optimised for the header dropdown.
 * Fires product + store queries in parallel.
 *
 * GIVEN search is made from the header search bar
 * WHEN an end-user is searching the store
 * THEN successful queries (0+ results) AND errors are returned to this function.
 *
 * @param {HydrateParams} params
 * @returns {Promise<HydrateResult>}
 */
export function hydrateTypeAhead(params) {
  return orchestrate('typeahead', { size: 8, ...params });
}

/**
 * PLP caller — full-page sizes.
 *
 * GIVEN a PLP is navigated to
 * WHEN an end-user is using the storefront
 * THEN successful queries (0+ results) AND errors are returned to this function.
 *
 * NOTE: no store module renders in the 'plp' context (the partner-stores
 * module only acts on 'slp' results), so PLP callers pass `skipStores: true`
 * and the `stores` envelope comes back `null`.
 *
 * @param {HydrateParams} params
 * @returns {Promise<HydrateResult>}
 */
export function hydratePLP(params) {
  return orchestrate('plp', { size: 30, ...params });
}

/**
 * SLP caller — fires on /search after the user submits the header form.
 *
 * GIVEN an SLP is rendered following a search being conducted
 * WHEN an end-user is searching the storefront and uses the Enter or Return key
 * THEN successful queries (0+ results) AND errors are returned to this function.
 *
 * @param {HydrateParams} params
 * @returns {Promise<HydrateResult>}
 */
export function hydrateSLP(params) {
  return orchestrate('slp', { size: 30, includePageTemplate: true, ...params });
}

/**
 * @typedef {Object} HydratePDPParams
 * @property {string|number}        [productId]    - A single APIM product ID (convenience for `productIds: [productId]`).
 * @property {Array<string|number>} [productIds]   - List of APIM product IDs to fetch (1–100).
 * @property {Array<string>}        [fields]       - Override the fields returned by the product-detail endpoint.
 * @property {Object}               [extra]        - Additional top-level body keys (distributorInfo, portalInfo, etc.).
 * @property {AbortSignal}          [signal]       - Abort signal forwarded to the query.
 *
 * @typedef {Object} HydratePDPResult
 * @property {'pdp'}                                                                  context
 * @property {string}                                                                 productId - The first resolved product ID (for convenience / logging).
 * @property {import('@theme/query-product-detail').QueryProductDetailResult | null}  product   - The product-detail envelope.
 */

/**
 * PDP caller — fetches detailed product data from the APIM `product-detail`
 * endpoint for the product currently being viewed (or any explicit ID list).
 *
 * Mirrors the hydratePLP / hydrateSLP pattern: returns a normalised envelope
 * and dispatches a `theme:pdp:hydrated` CustomEvent on `document` so consumer
 * components (e.g. the `<pdp-hydration>` section element) and analytics can
 * react without the caller threading callbacks around.
 *
 * GIVEN a PDP (or PDP-like surface such as a featured product) is rendered
 * WHEN the storefront needs detailed product data from the NG API
 * THEN a successful response (with product data) AND errors are returned to
 *      this function via the standard `{ ok, data, error }` envelope.
 *
 * @param {HydratePDPParams} params
 * @returns {Promise<HydratePDPResult>}
 */
export async function hydratePDP(params = {}) {
  const { productId, productIds, fields, extra, signal } = params;

  const locale = getApiLocale();

  /** @type {import('@theme/query-product-detail').QueryProductDetailResult} */
  let product;
  try {
    product = await queryProductDetail({ productId, productIds, fields, ...locale, extra, signal });
  } catch (error) {
    product = wrapThrown('Product detail', error);
  }

  const resolvedId =
    productId != null
      ? String(productId)
      : Array.isArray(productIds) && productIds.length
        ? String(productIds[0])
        : '';

  /** @type {HydratePDPResult} */
  const result = { context: 'pdp', productId: resolvedId, product };

  document.dispatchEvent(new CustomEvent('theme:pdp:hydrated', { detail: result }));

  return result;
}

/* ------------------------------------------------------------------ */
/*  Store List caller                                                  */
/* ------------------------------------------------------------------ */

/**
 * @typedef {Object} HydrateStoreListParams
 * @property {string}              [query]       - Search term. Empty string is allowed for unfiltered browse.
 * @property {number}              [page]        - 1-based page number.
 * @property {number}              [size]        - Results per page (default: 25).
 * @property {Object}              [filters]     - Filter map passed through to queryStoreList.
 * @property {Array<string>}       [aggregations]- Aggregation keys.
 * @property {Array<string>}       [fields]      - Fields to return.
 * @property {string}              [sort]        - Sort key (e.g. 'BEST_MATCH', 'STORE_NAME_A_TO_Z').
 * @property {Object}              [extra]       - Additional top-level body keys.
 * @property {AbortSignal}         [signal]      - Abort signal forwarded to the query.
 *
 * @typedef {Object} HydrateStoreListResult
 * @property {'store-list'}                                                            context
 * @property {string}                                                                  query
 * @property {import('@theme/query-store-list').QueryStoreListResult | null}           stores
 */

/**
 * Store List caller — fetches stores from the APIM store-search endpoint.
 *
 * Returns a normalised envelope and dispatches a `theme:store-list:hydrated`
 * CustomEvent on `document` so consumer components (e.g. `<hydrate-store-list>`)
 * can react without the caller threading callbacks around.
 *
 * @param {HydrateStoreListParams} params
 * @returns {Promise<HydrateStoreListResult>}
 */
export async function hydrateStoreList(params = {}) {
  const { query = '', size = 25, signal, ...rest } = params;

  const locale = getStoreApiLocale();

  /** @type {import('@theme/query-store-list').QueryStoreListResult} */
  let stores;
  try {
    stores = await queryStoreList({ query, size, signal, ...locale, ...rest });
  } catch (error) {
    stores = wrapThrown('Store list', error);
  }

  /** @type {HydrateStoreListResult} */
  const result = { context: 'store-list', query: typeof query === 'string' ? query : '', stores };

  trackStoreSearch(result.query, 'store-list', stores);

  document.dispatchEvent(new CustomEvent('theme:store-list:hydrated', { detail: result }));

  return result;
}

/* ------------------------------------------------------------------ */
/*  Store Detail caller                                                */
/* ------------------------------------------------------------------ */

/**
 * @typedef {Object} HydrateStoreDetailParams
 * @property {Array<string>|string} [volumeIds]  - APIM volume ID(s) to fetch.
 * @property {Array<string>}        [fields]     - Override the fields returned.
 * @property {Object}               [extra]      - Additional top-level body keys.
 * @property {AbortSignal}          [signal]     - Abort signal forwarded to the query.
 *
 * @typedef {Object} HydrateStoreDetailResult
 * @property {'store-detail'}                                                            context
 * @property {string}                                                                    volumeId - The first resolved volume ID (for convenience / logging).
 * @property {import('@theme/query-store-detail').QueryStoreDetailResult | null}         store    - The store-detail envelope.
 */

/**
 * Store Detail caller — fetches detailed store data from the APIM store-detail
 * endpoint for the store currently being viewed (or any explicit volume ID list).
 *
 * Mirrors the hydratePDP pattern: returns a normalised envelope and dispatches
 * a `theme:store-detail:hydrated` CustomEvent on `document` so consumer
 * components (e.g. `<hydrate-store-detail>`) can react.
 *
 * @param {HydrateStoreDetailParams} params
 * @returns {Promise<HydrateStoreDetailResult>}
 */
export async function hydrateStoreDetail(params = {}) {
  const { volumeIds, fields, extra, signal } = params;

  const locale = getApiLocale();

  /** @type {import('@theme/query-store-detail').QueryStoreDetailResult} */
  let store;
  try {
    store = await queryStoreDetail({ volumeIds, fields, ...locale, extra, signal });
  } catch (error) {
    store = wrapThrown('Store detail', error);
  }

  const resolvedId = Array.isArray(volumeIds) && volumeIds.length
    ? String(volumeIds[0])
    : typeof volumeIds === 'string'
      ? volumeIds
      : '';

  /** @type {HydrateStoreDetailResult} */
  const result = { context: 'store-detail', volumeId: resolvedId, store };

  trackStorePageView(resolvedId, store);

  document.dispatchEvent(new CustomEvent('theme:store-detail:hydrated', { detail: result }));

  return result;
}

/**
 * Emits the store page-view event — landing on a store's page, as opposed to
 * clicking through to it (`ng_store_click`) or leaving for the partner's own
 * site (`ng_store_link_off`).
 *
 * `ng_page_view` already fires on these pages, but its `pageType` is
 * `template.name` — the bare string "page" — and it carries no store identity,
 * so it cannot answer "which store was viewed". Emitted here rather than in
 * analytics.js because the store name only exists once this response resolves.
 *
 * @param {string} volumeId - The resolved volume ID for the page.
 * @param {Object} store    - The queryStoreDetail result envelope.
 */
function trackStorePageView(volumeId, store) {
  const record = store?.data?.store;

  track(ANALYTICS_EVENTS.STORE_PAGE_VIEW, {
    volumeId,
    // Paired with the interstitial's own `source` — the two are different
    // landings and a report that cannot tell them apart is misleading.
    source: 'store-detail',
    storeName: record?.derived?.title || record?.catalogName || record?.name || '',
    catalogId: record?.catalogId || '',
    productType: record?.productType || '',
    // A store with no destination link cannot be linked off to — knowing that
    // is what makes the link-off rate readable.
    hasLinkOff: !!(record?.wwwLink || record?.derived?.storePageUrl || record?.derived?.linkUrl),
    responseOk: store?.ok ?? false,
    errorMessage: store?.error?.message || '',
    lastSearch: getLastSearch(),
  });
}
